(function () {
  'use strict';
  if (window.LPProductDiscount) { window.LPProductDiscount.init(); return; }
  const instances = new Map();
  const storageKey = 'lpProductDiscountCode';
  const previewKey = 'lpProductDiscountPreviewCode';
  let cart = null, reading = null, writing = null, requestSerial = 0;
  const currentClaims = new Set();
  const savedPreviews = new Set();
  try {
    const preview = sessionStorage.getItem(previewKey);
    if (preview) savedPreviews.add(preview.toUpperCase());
  } catch (_) {}
  let savedCode = '';
  try { savedCode = sessionStorage.getItem(storageKey) || ''; } catch (_) {}
  if (!savedCode) {
    try { savedCode = decodeURIComponent(document.cookie.split('; ').find(value => value.startsWith('discount_code='))?.slice('discount_code='.length) || ''); } catch (_) {}
  }

  function codesFrom(data) {
    const codes = new Map();
    (data?.discount_codes || []).forEach(entry => {
      const code = typeof entry === 'string' ? entry : entry.code;
      if (code) codes.set(code.toUpperCase(), { code, applicable: entry.applicable });
    });
    const applications = [...(data?.cart_level_discount_applications || [])];
    (data?.items || []).forEach(item => (item.line_level_discount_allocations || []).forEach(allocation => applications.push(allocation.discount_application)));
    applications.forEach(entry => {
      if (entry?.type === 'discount_code' && entry.title) codes.set(entry.title.toUpperCase(), { code: entry.title, applicable: true });
    });
    return [...codes.values()];
  }
  function remember(code) {
    savedCode = code;
    if (!code) {
      savedPreviews.clear();
      try { sessionStorage.removeItem(previewKey); } catch (_) {}
    }
    try { code ? sessionStorage.setItem(storageKey, code) : sessionStorage.removeItem(storageKey); } catch (_) {}
  }
  function confirmedCodes(data) { return codesFrom(data).filter(entry => entry.applicable === true); }
  function configuredOffer(code) {
    return !!code && [...instances.values()].some(instance => instance.offerCode.toUpperCase() === code.toUpperCase());
  }
  function claimedPreview(data, code = savedCode) {
    // A claimed offer or an explicitly submitted configured offer may preview
    // an empty cart. Arbitrary saved strings cannot authorize price previews.
    if (!configuredOffer(code) || !Array.isArray(data?.items) || data.items.length) return false;
    if (currentClaims.has(code.toUpperCase()) || savedPreviews.has(code.toUpperCase())) return true;
    try {
      return JSON.parse(localStorage.getItem('lpHalloween2026Claim') || 'null')?.code?.toUpperCase() === code.toUpperCase()
        && localStorage.getItem('lpCardOfferDisabled')?.toUpperCase() !== code.toUpperCase();
    } catch (_) { return false; }
  }
  function acceptedCode(data, preferred = savedCode) {
    const codes = confirmedCodes(data);
    return codes.find(entry => entry.code.toUpperCase() === preferred.toUpperCase())?.code
      || codes[0]?.code || (claimedPreview(data, preferred) ? preferred : '');
  }
  function update(data, reset = false) {
    cart = data;
    if (savedCode && !confirmedCodes(data).some(entry => entry.code.toUpperCase() === savedCode.toUpperCase()) && !claimedPreview(data)) remember('');
    instances.forEach(instance => instance.accept(reset));
  }
  async function read() {
    if (reading) return reading;
    const serial = ++requestSerial;
    reading = (async () => {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(12000) });
      if (!response.ok) throw new Error('Unable to check discount. You can continue and enter it at checkout.');
      const data = await response.json();
      if (!Array.isArray(data?.items)) throw new Error('Unable to check discount. Please try again.');
      if (serial === requestSerial) update(data);
      return data;
    })().finally(() => { reading = null; });
    return reading;
  }
  async function apply(code, remove = false, replacing = savedCode) {
    if (writing) throw new Error('Please wait for your discount request to finish.');
    writing = (async () => {
      const current = await read();
      const existing = codesFrom(current).map(entry => entry.code).filter(value => value.toUpperCase() !== replacing.toUpperCase() && value.toUpperCase() !== code.toUpperCase());
      if (!remove) existing.push(code);
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart/update.js', {
        method: 'POST', credentials: 'same-origin', signal: AbortSignal.timeout(12000),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: existing.join(',') })
      });
      const data = await response.json();
      if (!response.ok || Number(data?.status) >= 400) throw new Error(typeof data?.description === 'string' ? data.description : 'Could not save this code. Please try again.');
      if (!Array.isArray(data?.items) || typeof data.total_price !== 'number') throw new Error('Unable to confirm this code. Please try again.');
      const applicable = confirmedCodes(data).some(entry => entry.code.toUpperCase() === code.toUpperCase());
      // The merchant-configured offer has a known preview formula before any
      // item exists. Never use this exception for an arbitrary typed code.
      const preview = !remove && !data.items.length && configuredOffer(code);
      if (preview) {
        savedPreviews.add(code.toUpperCase());
        try { sessionStorage.setItem(previewKey, code); } catch (_) {}
      }
      ++requestSerial;
      // Publish the actual accepted cart state, never the attempted invalid code.
      const accepted = remove ? '' : applicable || preview ? code : acceptedCode(data);
      remember(accepted);
      update(data, remove || applicable || preview);
      document.dispatchEvent(new CustomEvent('lp:discount-cart', { detail: { cart: data, code: accepted, preview: preview && !applicable } }));
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        try { publish(PUB_SUB_EVENTS.cartUpdate, { source: 'lp-product-discount', cartData: data }); } catch (_) {}
      }
      if (!remove && !applicable && !preview) {
        throw new Error(data.items.length
          ? 'This code is invalid or does not apply to your cart. Please check the code and its conditions.'
          : 'This code cannot be verified with an empty cart. Add eligible items and try again.');
      }
      return data;
    })().finally(() => { writing = null; });
    return writing;
  }
  function visible(element) {
    if (!element || element.closest('[hidden], [aria-hidden="true"]') || !element.getClientRects().length) return false;
    for (let node = element; node instanceof Element; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
    }
    return true;
  }
  function setClass(node, name, enabled) {
    // Writing an unchanged class still notifies the theme/app observers.
    if (node.classList.contains(name) !== enabled) node.classList.toggle(name, enabled);
  }
  function amount(text, currency) {
    const number = text.match(/\d[\d.,\s\u00a0\u202f]*/)?.[0];
    if (!number) return null;
    const parts = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).formatToParts(1234.56);
    const decimal = parts.find(part => part.type === 'decimal')?.value || '.';
    const group = parts.find(part => part.type === 'group')?.value;
    let normalized = number.replace(/[\s\u00a0\u202f]/g, '');
    if (group) normalized = normalized.split(group).join('');
    const value = Number(normalized.replace(decimal, '.'));
    return Number.isFinite(value) ? Math.round(value * 100) : null;
  }
  function mount(configNode) {
    const productConfig = JSON.parse(configNode.textContent);
    const sharedNode = document.querySelector('[data-lp-offer-config]');
    const activity = sharedNode ? JSON.parse(sharedNode.textContent) : productConfig.activity;
    const config = Object.assign({}, productConfig, activity || {});
    const section = document.getElementById(configNode.dataset.sectionId);
    const info = section?.querySelector('.product__info-container');
    if (!info || instances.has(section)) return;
    info.setAttribute('data-lp-discount-scope', '');
    const widget = document.createElement('div');
    widget.setAttribute('data-lp-discount-widget', '');
    const tagIcon = '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><path fill="currentColor" d="M20.6 3H14a2 2 0 0 0-1.4.6L3.6 12.6a2 2 0 0 0 0 2.8l5 5a2 2 0 0 0 2.8 0l9-9A2 2 0 0 0 21 10V4a1 1 0 0 0-1-1h.6ZM17 8a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Z"/></svg>';
    widget.innerHTML = '<div class="lp-discount__total" hidden><s></s><strong></strong><div class="lp-discount__tag" data-lp-discount-tag hidden>' + tagIcon + '<span></span></div></div>' +
      '<div class="lp-discount__entry"><input type="text" placeholder="Enter your discount code" aria-label="Discount code" autocomplete="off" spellcheck="false"><button type="button" data-apply>Apply</button></div>' +
      '<div class="lp-discount__tag lp-discount__notice" hidden>' + tagIcon + '<span></span></div>' +
      '<p class="lp-discount__message" role="status" aria-live="polite" hidden></p>';
    const input = widget.querySelector('input'), entry = widget.querySelector('.lp-discount__entry');
    const notice = widget.querySelector('.lp-discount__notice'), total = widget.querySelector('.lp-discount__total');
    const discountTag = widget.querySelector('[data-lp-discount-tag]');
    const message = widget.querySelector('.lp-discount__message');
    const priceDisplay = document.createElement('div');
    priceDisplay.setAttribute('data-lp-discount-price', '');
    priceDisplay.hidden = true;
    priceDisplay.append(total);
    let editing = false, busy = false, frame = null, disposed = false, displayedCode = '';
    const excludedPurchaseAreas = '.mw-tingle, quick-add-modal, .product-card-wrapper, .complementary-products';
    function messageText(text, error = false) {
      if (message.textContent !== text) message.textContent = text;
      message.hidden = !text;
      message.classList.toggle('is-error', error);
    }
    function pricePreview(source, code, applicable) {
      const raw = amount(source.textContent, config.currency);
      if (raw === null || code.toUpperCase() !== config.code.toUpperCase() || applicable === false && (cart?.items || []).length > 0 || config.scope === 'unconfirmed') return null;
      // Customeow can own additional charges outside the Shopify price node.
      // Keep its original total until those charges can be read reliably.
      if (config.giftLayout && config.scope === 'portrait_options' && info.querySelector('customeow-block-button') && !info.querySelector('.mw-option')) return null;
      let eligible = raw;
      if (config.scope === 'portrait_options' && info.querySelector('.mw-option')) {
        const sdk = window.mwApo?.instances?.().find(instance => String(instance.productId) === String(config.productId) && instance.mode === 'inline');
        if (!sdk?.getOptions || !sdk?.getValues) return null;
        const allowed = config.eligibleOptions.split(',').map(title => title.trim().toLowerCase());
        if (config.giftLayout) allowed.push('choose size', 'size', 'choose color', 'color');
        let excluded = 0;
        for (const option of sdk.getOptions()) {
          if (option.hidden || allowed.includes(option.title.trim().toLowerCase())) continue;
          for (const value of sdk.getValues(option.id).filter(value => value.selected && !value.hiddenByRule)) {
            const sdkPrice = Number(value.price);
            if (!Number.isFinite(sdkPrice) || sdkPrice < 0) return null;
            const card = [...info.querySelectorAll('[data-value-id]')].find(node => node.dataset.valueId === String(value.id));
            // APO leaves empty price placeholders on zero-price values. An
            // empty label is not an unknown surcharge; conditional rules may
            // instead populate another price label on the same value card.
            const nativePrice = [...(card?.querySelectorAll('.mw-option__price') || [])].find(node => node.textContent.trim());
            // The SDK can expose shop-currency prices; only read the displayed market surcharge.
            if (nativePrice) {
              if (nativePrice.textContent.includes('%')) return null;
              const surcharge = amount(nativePrice.textContent, config.currency);
              if (surcharge === null || /-\s*[^\d]*\d/.test(nativePrice.textContent)) return null;
              // Quantities only matter for an actual charge. Do not block a
              // zero-price selection because its optional quantity is blank.
              if (surcharge > 0 && card?.querySelector('input[type="number"]') && Number(card.querySelector('input[type="number"]').value) !== 1) return null;
              excluded += surcharge;
            } else if (sdkPrice !== 0) return null;
          }
        }
        eligible = Math.max(0, raw - excluded);
      }
      return { original: raw, discounted: raw - Math.round(eligible * Number(config.percent) / 100) };
    }
    function render() {
      if (disposed) return;
      const purchaseExclusions = config.giftLayout ? excludedPurchaseAreas : '.mw-tingle, quick-add-modal';
      const apoRows = [...info.querySelectorAll('.mw-group__button')].filter(row => !row.closest(purchaseExclusions) && visible(row));
      const hasApo = [...info.querySelectorAll('.mw-option')].some(option => !option.closest(purchaseExclusions));
      const giftRow = config.giftLayout && [...info.querySelectorAll('.mw-apo-add-to-cart-custom, .mw-apo-add-to-cart-cloned, customeow-block-button')]
        .find(node => !node.closest(excludedPurchaseAreas) && visible(node));
      const row = apoRows[0] || giftRow || (!hasApo && [...info.querySelectorAll('.product-form__buttons')].find(node => !node.closest(purchaseExclusions) && visible(node)));
      const priceHost = info.querySelector('[data-lp-price-under-title]');
      if (!priceHost) { widget.remove(); priceDisplay.remove(); return; }
      if (priceDisplay.parentElement !== priceHost) priceHost.append(priceDisplay);
      // Discount entry stays in the purchase area; only its amount lives above.
      if (row) {
        if (widget.nextElementSibling !== row) row.before(widget);
      } else widget.remove();
      if (config.giftLayout && priceHost) {
        // APO writes the calculated total into the sale node while Shopify's
        // regular node remains the base price. Only gift APO instances use this
        // marker; other products keep their existing sale/regular presentation.
        const giftApo = hasApo && window.mwApo?.instances?.().some(instance =>
          String(instance.productId) === String(config.productId) && instance.mode === 'inline');
        setClass(priceHost, 'lp-gift-apo-total', !!giftApo);
      }
      const codes = confirmedCodes(cart);
      const chosen = codes.find(value => value.code.toUpperCase() === config.code.toUpperCase()) || codes.find(value => value.code.toUpperCase() === savedCode.toUpperCase()) || codes[0];
      const pending = !chosen && savedCode.toUpperCase() === config.code.toUpperCase() && claimedPreview(cart);
      const code = chosen?.code || (pending ? savedCode : '');
      displayedCode = code || '';
      entry.hidden = !!code && !editing;
      if (notice.querySelector('span').textContent !== (code || '')) notice.querySelector('span').textContent = code || '';
      if (code && !editing && document.activeElement !== input) input.value = code;
      const candidates = [...(priceHost || info).querySelectorAll('.price__sale .price-item--sale, .price__regular .price-item--regular')];
      const source = candidates.find(node => !node.closest('.mw-option, quick-add-modal, .mw-tingle, .product-card-wrapper') && (visible(node) || node.classList.contains('lp-discount-total-source') && visible(node.parentElement)));
      const preview = code && source ? pricePreview(source, code, chosen?.applicable) : null;
      info.querySelectorAll('.price-item--sale, .price-item--regular').forEach(node => setClass(node, 'lp-discount-total-source', node === source && !!preview));
      total.hidden = !preview;
      priceDisplay.hidden = !preview;
      notice.hidden = !code || !!preview;
      discountTag.hidden = !preview || preview.original <= preview.discounted;
      if (preview) {
        const format = cents => window.LPMoney?.format(cents, { currency: config.currency }) || new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: config.currency }).format(cents / 100);
        const values = [format(preview.original), format(preview.discounted)];
        ['s', 'strong'].forEach((selector, i) => { if (total.querySelector(selector).textContent !== values[i]) total.querySelector(selector).textContent = values[i]; });
        const tagText = code + ' (-' + format(preview.original - preview.discounted) + ')';
        if (discountTag.querySelector('span').textContent !== tagText) discountTag.querySelector('span').textContent = tagText;
      }
    }
    function schedule() { if (!frame && !disposed) frame = requestAnimationFrame(() => { frame = null; render(); }); }
    async function submit(remove = false) {
      const code = (remove ? displayedCode : input.value).trim().toUpperCase();
      if (!code || busy) return;
      busy = true;
      widget.querySelectorAll('button').forEach(button => { button.disabled = true; });
      messageText(remove ? 'Removing code...' : 'Checking code...');
      try {
        await apply(code, remove, displayedCode);
        editing = false;
        if (remove) { input.value = ''; messageText('Code removed.'); }
        else {
          messageText('');
        }
      } catch (error) { editing = true; messageText(error.message, true); }
      finally { busy = false; widget.querySelectorAll('button').forEach(button => { button.disabled = false; }); render(); }
    }
    widget.querySelector('[data-apply]').addEventListener('click', () => submit());
    input.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); event.stopPropagation(); submit(); } });
    const observer = new MutationObserver(changes => {
      if (changes.some(change => !widget.contains(change.target) && !priceDisplay.contains(change.target))) schedule();
    });
    observer.observe(info, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
    info.addEventListener('change', schedule);
    instances.set(section, { offerCode: config.code, render, linkError(code, text) { editing = true; input.value = code; messageText(text, true); render(); }, accept(reset = false) { if (reset && !busy) { editing = false; messageText(''); } render(); }, dispose() {
      disposed = true; observer.disconnect(); cancelAnimationFrame(frame); info.removeEventListener('change', schedule);
      info.querySelectorAll('.lp-discount-total-source').forEach(node => node.classList.remove('lp-discount-total-source'));
      widget.remove(); priceDisplay.remove(); info.removeAttribute('data-lp-discount-scope'); instances.delete(section);
      info.querySelector('[data-lp-price-under-title]')?.classList.remove('lp-gift-apo-total');
    } });
    render();
  }
  let linkStarted = false;
  function discountLinkIntent() {
    const url = new URL(window.location.href);
    const explicit = (url.searchParams.get('lp_discount') || '').trim().toUpperCase();
    let cookie = '';
    try { cookie = decodeURIComponent(document.cookie.split('; ').find(value => value.startsWith('discount_code='))?.slice('discount_code='.length) || '').trim().toUpperCase(); } catch (_) {}
    const code = explicit || cookie;
    if (!configuredOffer(code)) return null;
    if (!explicit) {
      try {
        if (sessionStorage.getItem('lpDiscountLinkHandled') === code || localStorage.getItem('lpCardOfferDisabled')?.toUpperCase() === code) return null;
      } catch (_) {}
    }
    return { code, explicit: !!explicit };
  }
  async function syncDiscountLink(intent) {
    try {
      const data = await read();
      if (confirmedCodes(data).some(entry => entry.code.toUpperCase() === intent.code)) {
        remember(intent.code); update(data, true);
      } else {
        // Submit to Shopify before authorizing the configured empty-cart preview.
        // Keep other cart codes; never infer applicability from the link alone.
        await apply(intent.code, false, '');
      }
      try { sessionStorage.setItem('lpDiscountLinkHandled', intent.code); } catch (_) {}
      if (intent.explicit) {
        const url = new URL(window.location.href);
        url.searchParams.delete('lp_discount');
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      }
    } catch (error) {
      instances.forEach(instance => instance.linkError(intent.code, error.message));
    }
  }
  function redirectCampaignProduct() {
    const url = new URL(window.location.href);
    const match = url.pathname.match(/^\/(?:([a-z]{2}(?:-[a-z]{2})?)\/)?products\/(the-queen-hand-painted-pet-portrait|realistic-pet-portrait-hand-painted-pet-portrait)\/?$/i);
    if (!match || window.Shopify?.designMode || !configuredOffer('LUMA20')) return false;
    const key = 'lpProductCampaignRedirect:' + url.pathname;
    // The return parameter also prevents a loop if browser storage is blocked.
    if (url.searchParams.has('lp_discount')) {
      try { sessionStorage.setItem(key, 'LUMA20'); } catch (_) {}
      window.history.replaceState(Object.assign({}, window.history.state, { lpProductCampaignRedirect: key }), '', url.href);
      return false;
    }
    if (window.history.state?.lpProductCampaignRedirect === key) return false;
    try {
      if (sessionStorage.getItem(key) === 'LUMA20') return false;
      sessionStorage.setItem(key, 'LUMA20');
    } catch (_) {}
    url.searchParams.set('lp_discount', 'LUMA20');
    const root = window.Shopify?.routes?.root || (match[1] ? '/' + match[1] + '/' : '/');
    window.location.replace(root + 'discount/LUMA20?redirect=' + encodeURIComponent(url.pathname + url.search + url.hash));
    return true;
  }
  function init() {
    document.querySelectorAll('[data-lp-discount-config]').forEach(mount);
    if (instances.size && redirectCampaignProduct()) return;
    if (instances.size && !linkStarted) {
      const intent = discountLinkIntent();
      if (intent) { linkStarted = true; syncDiscountLink(intent); return; }
    }
    if (instances.size && !cart) read().catch(() => {});
  }
  window.LPProductDiscount = { init, apply, read };
  document.addEventListener('lp:discount-cart', event => {
    if (Array.isArray(event.detail?.cart?.items)) {
      remember(event.detail.code || ''); ++requestSerial;
      update(event.detail.cart, !!acceptedCode(event.detail.cart));
    }
  });
  document.addEventListener('lp:card-offer-claimed', event => {
    const code = event.detail?.code;
    if (typeof code !== 'string' || ![...instances.values()].some(instance => instance.offerCode.toUpperCase() === code.toUpperCase())) return;
    currentClaims.add(code.toUpperCase());
    if (Array.isArray(cart?.items) && !cart.items.length) { remember(code); instances.forEach(instance => instance.accept(true)); }
  });
  document.addEventListener('mwApo::recalculated', () => instances.forEach(instance => instance.render()));
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', event => instances.forEach((instance, section) => { if (event.target.contains(section)) instance.dispose(); }));
  window.addEventListener('pageshow', () => { if (instances.size) read().catch(() => {}); });
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, event => { if (event.source !== 'lp-product-discount' && instances.size) read().catch(() => {}); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
})();
